// Category → Manufacturer → Model picker for BYOM uploads.
// Static catalogue lives in equipmentConfig.js; "Other" (or the Other
// category) switches to free-text inputs. Calls onChange with the built
// equipment_path (e.g. "main_engine/man-b-w/me-gi") or null while incomplete.
import { useEffect, useState } from 'react';
import { EQUIPMENT_CATEGORIES, OTHER_KEY, buildEquipmentPath } from './equipmentConfig';

const labelStyle = {
  fontFamily: 'var(--mono)',
  fontSize: 10,
  letterSpacing: 1.5,
  color: 'var(--text-3)',
  marginBottom: 6,
};

const selectStyle = {
  width: '100%',
  background: 'var(--surface-1, #ffffff)',
  border: '1px solid var(--border)',
  borderRadius: 10,
  padding: '12px 14px',
  color: 'var(--text)',
  fontSize: 13.5,
  fontFamily: 'var(--sans)',
  outline: 'none',
};

function Field({ label, children }) {
  return (
    <div style={{ flex: '1 1 180px', minWidth: 150 }}>
      <div style={labelStyle}>{label}</div>
      {children}
    </div>
  );
}

export default function EquipmentWizard({ onChange }) {
  const [categoryKey, setCategoryKey] = useState('');
  const [manufacturerKey, setManufacturerKey] = useState('');
  const [modelKey, setModelKey] = useState('');
  const [customManufacturer, setCustomManufacturer] = useState('');
  const [customModel, setCustomModel] = useState('');

  const category = EQUIPMENT_CATEGORIES.find(c => c.key === categoryKey) || null;
  const manufacturers = category?.manufacturers || [];
  const manufacturer = manufacturers.find(m => m.key === manufacturerKey) || null;
  const models = manufacturer?.models || [];

  const manufacturerIsCustom =
    categoryKey && (manufacturers.length === 0 || manufacturerKey === OTHER_KEY);
  const modelIsCustom =
    categoryKey && (manufacturerIsCustom || (manufacturerKey && modelKey === OTHER_KEY));

  const manufacturerName = manufacturerIsCustom ? customManufacturer : manufacturer?.label || '';
  const modelName = modelIsCustom ? customModel : modelKey;

  useEffect(() => {
    onChange?.(
      categoryKey ? buildEquipmentPath(categoryKey, manufacturerName, modelName) : null,
      categoryKey
        ? {
            category: category?.label,
            manufacturer: manufacturerName,
            model: modelName,
          }
        : null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey, manufacturerName, modelName]);

  function pickCategory(key) {
    setCategoryKey(key);
    setManufacturerKey('');
    setModelKey('');
    setCustomManufacturer('');
    setCustomModel('');
  }

  return (
    <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      <Field label="EQUIPMENT TYPE">
        <select
          style={selectStyle}
          value={categoryKey}
          onChange={e => pickCategory(e.target.value)}
        >
          <option value="">Select…</option>
          {EQUIPMENT_CATEGORIES.map(c => (
            <option key={c.key} value={c.key}>
              {c.label}
            </option>
          ))}
        </select>
      </Field>

      {categoryKey && (
        <Field label="MANUFACTURER">
          {manufacturers.length > 0 && !manufacturerIsCustom ? (
            <select
              style={selectStyle}
              value={manufacturerKey}
              onChange={e => {
                setManufacturerKey(e.target.value);
                setModelKey('');
                setCustomModel('');
              }}
            >
              <option value="">Select…</option>
              {manufacturers.map(m => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
              <option value={OTHER_KEY}>Other…</option>
            </select>
          ) : (
            <input
              className="text-input"
              style={{ width: '100%' }}
              placeholder="Manufacturer name"
              value={customManufacturer}
              onChange={e => setCustomManufacturer(e.target.value)}
            />
          )}
        </Field>
      )}

      {categoryKey && (manufacturerKey || manufacturerIsCustom) && (
        <Field label="MODEL">
          {!modelIsCustom && models.length > 0 ? (
            <select
              style={selectStyle}
              value={modelKey}
              onChange={e => setModelKey(e.target.value)}
            >
              <option value="">Select…</option>
              {models.map(m => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
              <option value={OTHER_KEY}>Other…</option>
            </select>
          ) : (
            <input
              className="text-input"
              style={{ width: '100%' }}
              placeholder="Model (e.g. ME-GI, P 615)"
              value={customModel}
              onChange={e => setCustomModel(e.target.value)}
            />
          )}
        </Field>
      )}
    </div>
  );
}
